import { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { getEstimate, updateEstimate } from '../../services/estimate-service';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { PDFDocument } from 'pdf-lib';
import app from '../../services/firebase-init';
import { useNotification } from '../../components/NotificationModal';
import AppLayout from '../../components/AppLayout';
import Button from '../../components/ui/Button';
import LoadingSpinner from '../../components/ui/LoadingSpinner';
import { ArrowLeft, Save, Download, Send, Loader2, CheckCircle2, FileText, X } from 'lucide-react';
import { noAutofill, noAutofillContainer } from '../../utils/noAutofill';

const INK    = '#2B2620';
const WALNUT = '#8B4513';
const MOSS   = '#3B6B44';
const STONE  = '#8C8275';
const PAPER  = '#FDFBF8';

const FONT_DISPLAY = "'Fraunces', Georgia, serif";
const FONT_SANS    = "'IBM Plex Sans', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif";

const MONTHS = ['January','February','March','April','May','June','July','August','September','October','November','December'];

function fmtPhone(v) {
  const d = v.replace(/\D/g, '');
  if (d.length <= 3) return d;
  if (d.length <= 6) return `(${d.slice(0,3)}) ${d.slice(3)}`;
  return `(${d.slice(0,3)}) ${d.slice(3,6)}-${d.slice(6,10)}`;
}

function fmtMoney(v) {
  const n = parseFloat(v);
  if (isNaN(n)) return v || '';
  return n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

// ── Signature Pad ────────────────────────────────────────────────────────
function SignaturePad({ value, onChange, width = 300, height = 80 }) {
  const canvasRef = useRef(null);
  const drawing   = useRef(false);
  const lastPos   = useRef(null);

  useEffect(() => {
    if (!value && canvasRef.current)
      canvasRef.current.getContext('2d').clearRect(0, 0, width, height);
  }, [value, width, height]);

  function getPos(e, canvas) {
    const r = canvas.getBoundingClientRect();
    const src = e.touches ? e.touches[0] : e;
    return {
      x: (src.clientX - r.left) * (width / r.width),
      y: (src.clientY - r.top)  * (height / r.height),
    };
  }
  function start(e) {
    e.preventDefault();
    drawing.current = true;
    lastPos.current = getPos(e, canvasRef.current);
  }
  function move(e) {
    e.preventDefault();
    if (!drawing.current) return;
    const ctx = canvasRef.current.getContext('2d');
    const pos = getPos(e, canvasRef.current);
    ctx.beginPath();
    ctx.moveTo(lastPos.current.x, lastPos.current.y);
    ctx.lineTo(pos.x, pos.y);
    ctx.strokeStyle = INK;
    ctx.lineWidth = 1.8;
    ctx.lineCap = 'round';
    ctx.stroke();
    lastPos.current = pos;
    onChange(canvasRef.current.toDataURL('image/png'));
  }
  function end() { drawing.current = false; }
  function clear() {
    canvasRef.current.getContext('2d').clearRect(0, 0, width, height);
    onChange('');
  }

  return (
    <div>
      <div style={{ border: `2px solid ${value ? WALNUT : '#e0d5cc'}`, borderRadius: '8px', background: '#fefefe', position: 'relative', overflow: 'hidden', width, height }}>
        <canvas ref={canvasRef} width={width} height={height}
          style={{ display: 'block', cursor: 'crosshair', touchAction: 'none' }}
          onMouseDown={start} onMouseMove={move} onMouseUp={end} onMouseLeave={end}
          onTouchStart={start} onTouchMove={move} onTouchEnd={end}
        />
        {!value && (
          <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', pointerEvents: 'none', color: '#ccc', fontSize: '12px', fontStyle: 'italic' }}>
            Draw signature here
          </div>
        )}
      </div>
      {value && (
        <button onClick={clear} style={{ display: 'flex', alignItems: 'center', gap: '4px', background: 'none', border: 'none', color: '#A13D2D', fontSize: '11px', cursor: 'pointer', padding: '3px 0', fontFamily: FONT_SANS }}>
          <X size={11} strokeWidth={2} /> Clear
        </button>
      )}
    </div>
  );
}

function F({ label, children }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
      <div style={{ fontSize: '10px', fontWeight: '700', color: STONE, textTransform: 'uppercase', letterSpacing: '0.5px' }}>{label}</div>
      {children}
    </div>
  );
}

const inp = {
  width: '100%', padding: '8px 11px', border: '1.5px solid #e5e5e5',
  borderRadius: '8px', fontSize: '13px', fontFamily: FONT_SANS,
  boxSizing: 'border-box', outline: 'none', background: 'white',
};
const sel = { ...inp, cursor: 'pointer' };

function CB({ label, checked, onChange }) {
  return (
    <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', fontSize: '13px', color: '#333', padding: '3px 0' }}>
      <div onClick={() => onChange(!checked)} style={{
        width: '18px', height: '18px', borderRadius: '4px', flexShrink: 0,
        border: `2px solid ${checked ? WALNUT : '#d1d5db'}`,
        background: checked ? WALNUT : 'white',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}>
        {checked && <svg width="10" height="8" viewBox="0 0 10 8" fill="none"><path d="M1 4L3.5 7L9 1" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>}
      </div>
      <span onClick={() => onChange(!checked)}>{label}</span>
    </label>
  );
}

function Card({ title, children }) {
  return (
    <div style={{ background: PAPER, borderRadius: '12px', padding: '16px 18px', border: '1px solid rgba(139,69,19,0.1)', boxShadow: '0 2px 12px rgba(139,69,19,0.06)' }}>
      {title && (
        <div style={{ fontSize: '11px', fontWeight: '800', color: WALNUT, textTransform: 'uppercase', letterSpacing: '0.8px', marginBottom: '12px', paddingBottom: '8px', borderBottom: '2px solid #f0e8e0' }}>
          {title}
        </div>
      )}
      {children}
    </div>
  );
}

// ── Fill PDF with pdf-lib ─────────────────────────────────────────────────
async function fillContractPDF(fields, repSigDataUrl) {
  const response = await fetch('/assets/contract/Contract.pdf');
  const pdfBytes = await response.arrayBuffer();
  const pdfDoc   = await PDFDocument.load(pdfBytes, { ignoreEncryption: true });
  const form     = pdfDoc.getForm();

  function setTextField(name, value) {
    try { form.getTextField(name).setText(value || ''); }
    catch(e) { console.warn('TextField not found:', name); }
  }
  function setDropdown(name, value) {
    try {
      const f = form.getDropdown(name);
      if (value && f.getOptions().includes(value)) f.select(value);
    } catch(e) { console.warn('Dropdown not found:', name); }
  }
  function setCheckbox(name, checked) {
    try {
      const f = form.getCheckBox(name);
      if (checked) f.check(); else f.uncheck();
    } catch(e) { console.warn('Checkbox not found:', name); }
  }

  setTextField('contract number',                       fields.contractNumber);
  setTextField('Purchaser',                             fields.purchaserName);
  setTextField('Address',                               fields.address);
  setTextField('CitystZip 1',                           fields.cityStateZip);
  setTextField('Email',                                 fields.email);
  setTextField('Home Phone',                            fields.homePhone);
  setTextField('Cell Phone',                            fields.cellPhone);
  setTextField('Work Phone',                            fields.workPhone);
  setTextField('Fax Number',                            fields.faxNumber);
  setTextField('Project Description',                   fields.projectDescription);
  setTextField('alumn wrap Color',                      fields.wrapColor);
  setTextField('1 Cash Price including sales tax',      fmtMoney(fields.cashPrice));
  setTextField('2 Cash Down Payment',                   fmtMoney(fields.downPayment));
  setTextField('3 UNPAID BALANCE  DUE UPON COMPLETION', fmtMoney(fields.unpaidBalance));
  setTextField('date',                                  fields.sigDay);
  setTextField('20',                                    fields.sigYear);

  setDropdown('trash',                  fields.trash);
  setDropdown('apprx installtion date', fields.installMonth);
  setDropdown('month of signing',       fields.sigMonth);

  setCheckbox('Low E argon gas', fields.cbLowE);
  setCheckbox('gas',             fields.cbScreen);
  setCheckbox('outside trim',    fields.cbOutsideTrim);
  setCheckbox('brickmold',       fields.cbBrickmold);
  setCheckbox('1x4',             fields.cbOneByFour);

  // Embed rep signature image
  if (repSigDataUrl) {
    try {
      const base64   = repSigDataUrl.split(',')[1];
      const sigBytes = Uint8Array.from(atob(base64), c => c.charCodeAt(0));
      const sigImage = await pdfDoc.embedPng(sigBytes);
      const sigField = form.getField('Represenative signature');
      const widgets  = sigField.acroField.getWidgets();
      if (widgets.length > 0) {
        const rect = widgets[0].getRectangle();
        pdfDoc.getPage(0).drawImage(sigImage, { x: rect.x, y: rect.y, width: rect.width, height: rect.height });
      }
    } catch(e) { console.warn('Could not embed rep signature:', e.message); }
  }

  return new Blob([await pdfDoc.save()], { type: 'application/pdf' });
}

// ════════════════════════════════════════════════════════════════════════════
export default function ContractBuilderPage() {
  const { estimateId } = useParams();
  const navigate       = useNavigate();

  const [estimate,     setEstimate]     = useState(null);
  const notify = useNotification();
  const [loading,      setLoading]      = useState(true);
  const [generating,   setGenerating]   = useState(false);
  const [sending,      setSending]      = useState(false);
  const [showSuccess,  setShowSuccess]  = useState(false);
  const [previewUrl,   setPreviewUrl]   = useState(null);
  const [previewBlob,  setPreviewBlob]  = useState(null);

  // form fields
  const [contractNumber,     setContractNumber]     = useState('');
  const [customer,           setCustomer]           = useState({ name: '', address: '', cityStateZip: '', email: '', homePhone: '', cellPhone: '', workPhone: '', faxNumber: '' });
  const [projectDescription, setProjectDescription] = useState('');
  const [checkboxes,         setCheckboxes]         = useState({ lowE: false, screen: false, outsideTrim: false, brickmold: false, oneByFour: false });
  const [installMonth,       setInstallMonth]       = useState('');
  const [wrapColor,          setWrapColor]          = useState('White');
  const [trash,              setTrash]              = useState('Yes');
  const [cashPrice,          setCashPrice]          = useState('');
  const [downPayment,        setDownPayment]        = useState('');
  const [unpaidBalance,      setUnpaidBalance]      = useState('');
  const [sigDay,             setSigDay]             = useState('');
  const [sigMonth,           setSigMonth]           = useState('');
  const [sigYear,            setSigYear]            = useState('');
  const [repSignature,       setRepSignature]       = useState('');

  // refs to avoid stale closures without adding to useEffect deps
  const debounceRef   = useRef(null);
  const prevUrlRef    = useRef(null);
  const fieldsRef     = useRef({});
  const repSigRef     = useRef('');
  const isLoadingRef  = useRef(true);

  // keep refs in sync
  useEffect(() => {
    fieldsRef.current = {
      contractNumber, purchaserName: customer.name, address: customer.address,
      cityStateZip: customer.cityStateZip, email: customer.email,
      homePhone: customer.homePhone, cellPhone: customer.cellPhone,
      workPhone: customer.workPhone, faxNumber: customer.faxNumber,
      projectDescription, installMonth, wrapColor, trash,
      cashPrice, downPayment, unpaidBalance,
      sigDay, sigMonth, sigYear,
      cbLowE: checkboxes.lowE, cbScreen: checkboxes.screen,
      cbOutsideTrim: checkboxes.outsideTrim, cbBrickmold: checkboxes.brickmold,
      cbOneByFour: checkboxes.oneByFour,
    };
  });
  useEffect(() => { repSigRef.current = repSignature; }, [repSignature]);

  // ── Load estimate ────────────────────────────────────────────────────
  useEffect(() => {
    async function load() {
      const est = await getEstimate(estimateId);
      if (!est) { navigate('/estimates'); return; }
      setEstimate(est);
      const c = est.customer || {};
      setCustomer({
        name:         c.name         || '',
        address:      c.address      || '',
        cityStateZip: c.cityStateZip || c.city || '',
        email:        c.email        || '',
        homePhone:    c.phone        || c.homePhone || '',
        cellPhone:    c.cellPhone    || c.cell      || '',
        workPhone:    c.workPhone    || '',
        faxNumber:    c.faxNumber    || '',
      });
      const total = Number(est.grandTotal) || 0;
      const down  = Math.round((total / 3) * 100) / 100;
      setCashPrice(total.toFixed(2));
      setDownPayment(down.toFixed(2));
      setUnpaidBalance((total - down).toFixed(2));
      const today = new Date();
      setSigDay(String(today.getDate()));
      setSigMonth(MONTHS[today.getMonth()]);
      setSigYear(String(today.getFullYear()).slice(2));
      setInstallMonth(MONTHS[Math.min(today.getMonth() + 1, 11)]);

      if (est.contract) {
        const co = est.contract;
        if (co.contractNumber)     setContractNumber(co.contractNumber);
        if (co.projectDescription) setProjectDescription(co.projectDescription);
        if (co.checkboxes)         setCheckboxes(co.checkboxes);
        if (co.installMonth)       setInstallMonth(co.installMonth);
        if (co.wrapColor)          setWrapColor(co.wrapColor);
        if (co.trash)              setTrash(co.trash);
        if (co.cashPrice)          setCashPrice(co.cashPrice);
        if (co.downPayment)        setDownPayment(co.downPayment);
        if (co.unpaidBalance)      setUnpaidBalance(co.unpaidBalance);
        if (co.sigDay)             setSigDay(co.sigDay);
        if (co.sigMonth)           setSigMonth(co.sigMonth);
        if (co.sigYear)            setSigYear(co.sigYear);
        if (co.repSignature)       setRepSignature(co.repSignature);
        if (co.customer)           setCustomer(co.customer);
      }
      isLoadingRef.current = false;
      setLoading(false);
      // trigger initial preview after a short delay
      setTimeout(() => schedulePreview(), 300);
    }
    load();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [estimateId]);

  // ── Auto-calc unpaid balance ─────────────────────────────────────────
  useEffect(() => {
    const p = parseFloat(cashPrice)   || 0;
    const d = parseFloat(downPayment) || 0;
    setUnpaidBalance((p - d).toFixed(2));
  }, [cashPrice, downPayment]);

  // ── Schedule debounced preview (stable function using refs) ──────────
  function schedulePreview() {
    if (isLoadingRef.current) return;
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      setGenerating(true);
      try {
        const blob = await fillContractPDF(fieldsRef.current, repSigRef.current);
        const url  = URL.createObjectURL(blob);
        if (prevUrlRef.current) URL.revokeObjectURL(prevUrlRef.current);
        prevUrlRef.current = url;
        setPreviewUrl(url);
        setPreviewBlob(blob);
      } catch(e) { console.error('Preview error:', e); }
      setGenerating(false);
    }, 800);
  }

  // ── Trigger preview on any field change ──────────────────────────────
  useEffect(() => { schedulePreview(); },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [contractNumber, customer.name, customer.address, customer.cityStateZip,
     customer.email, customer.homePhone, customer.cellPhone, customer.workPhone,
     customer.faxNumber, projectDescription, installMonth, wrapColor, trash,
     cashPrice, downPayment, unpaidBalance, sigDay, sigMonth, sigYear,
     checkboxes.lowE, checkboxes.screen, checkboxes.outsideTrim,
     checkboxes.brickmold, checkboxes.oneByFour, repSignature]
  );

  // ── Download ─────────────────────────────────────────────────────────
  function handleDownload() {
    if (!previewBlob) return;
    const a = document.createElement('a');
    a.href = URL.createObjectURL(previewBlob);
    a.download = `Contract_${customer.name || 'Draft'}.pdf`;
    a.click();
  }

  // ── Save draft ───────────────────────────────────────────────────────
  async function handleSaveDraft() {
    const data = {
      contractNumber, customer, projectDescription, checkboxes, installMonth,
      wrapColor, trash, cashPrice, downPayment, unpaidBalance,
      sigDay, sigMonth, sigYear, repSignature, contractStatus: 'draft',
    };
    await updateEstimate(estimateId, { contract: data });
    notify.success('Draft saved', 'Your contract draft has been saved.');
  }

  // ── Send to customer ─────────────────────────────────────────────────
  async function handleSend() {
    if (!contractNumber.trim()) { notify.warning('Contract number required', 'Please enter a contract number before sending.'); return; }
    if (!customer.name.trim())  { notify.warning('Customer name required', 'Please enter the customer name before sending.'); return; }
    if (!customer.email.trim()) { notify.warning('Customer email required', 'Please enter the customer email before sending.'); return; }
    if (!repSignature)          { notify.warning('Signature required', 'Please draw your signature before sending.'); return; }
    if (!previewBlob)           { notify.info('Preview generating', 'Wait for the contract preview to finish generating, then try again.'); return; }
    setSending(true);
    try {
      const contractToken = `ct_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
      const data = {
        contractNumber, customer, projectDescription, checkboxes, installMonth,
        wrapColor, trash, cashPrice, downPayment, unpaidBalance,
        sigDay, sigMonth, sigYear, repSignature,
        contractToken, contractStatus: 'sent',
        contractSentAt: new Date().toISOString(),
      };
      await updateEstimate(estimateId, { contract: data });
      const fns = getFunctions(app);
      await httpsCallable(fns, 'sendContractToCustomer')({
        estimateId, contractToken, contractNumber,
        customerName:  customer.name,
        customerEmail: customer.email,
        customerPhone: customer.cellPhone || customer.homePhone,
        cashPrice: fmtMoney(cashPrice),
        repName:  estimate?.createdByName,
        repPhone: estimate?.createdByPhone,
        repEmail: estimate?.createdByEmail,
      });
      setShowSuccess(true);
    } catch(e) {
      console.error(e);
      notify.error('Could not send contract', 'The contract did not send. Try again — if it persists, contact Kwizera.', e?.message);
    }
    setSending(false);
  }

  const breadcrumb = 'Dashboard › Estimates › Create Contract';

  return (
    <AppLayout
      title="Create Contract"
      breadcrumb={breadcrumb}
      topBarActions={
        !loading && !showSuccess ? (
          <>
            <Button variant="secondary" size="small" onClick={handleSaveDraft}><Save size={14} strokeWidth={2} /> Save Draft</Button>
            {previewBlob && <Button variant="secondary" size="small" onClick={handleDownload}><Download size={14} strokeWidth={2} /> Download</Button>}
            <Button size="small" onClick={handleSend} disabled={sending}>
              {sending ? <Loader2 size={14} strokeWidth={2} style={{ animation: 'spin 0.8s linear infinite' }} /> : <Send size={14} strokeWidth={2} />} {sending ? 'Sending…' : 'Send to Customer'}
            </Button>
          </>
        ) : null
      }
    >
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>

      {loading ? (
        <div style={{ textAlign: 'center', padding: '100px 20px' }}>
          <LoadingSpinner size="medium" color={WALNUT} />
        </div>
      ) : showSuccess ? (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '60px 24px', fontFamily: FONT_SANS }}>
          <div style={{ background: PAPER, borderRadius: '20px', padding: '48px 36px', maxWidth: '440px', width: '100%', textAlign: 'center', border: '1px solid rgba(139,69,19,0.1)', boxShadow: '0 4px 24px rgba(0,0,0,0.08)' }}>
            <CheckCircle2 size={44} strokeWidth={1.5} color={MOSS} style={{ marginBottom: '16px' }} />
            <div style={{ fontFamily: FONT_DISPLAY, fontSize: '22px', fontWeight: 600, color: INK, marginBottom: '10px' }}>Contract Sent!</div>
            <div style={{ fontSize: '14px', color: STONE, lineHeight: '1.6', marginBottom: '28px' }}>
              {customer.name} will receive an email with a link to sign the contract. Once they sign, the contract locks and they can only view the signed PDF.
            </div>
            <Button onClick={() => navigate(`/estimates/edit/${estimateId}`)}>Back to Estimate</Button>
          </div>
        </div>
      ) : (
        <div style={{ fontFamily: FONT_SANS }}>
          <button
            onClick={() => navigate(`/estimates/edit/${estimateId}`)}
            style={{ display: 'flex', alignItems: 'center', gap: '6px', background: 'none', border: 'none', color: WALNUT, fontSize: '13px', fontWeight: '600', cursor: 'pointer', fontFamily: FONT_SANS, padding: '4px 0', marginBottom: '10px' }}
          >
            <ArrowLeft size={13} strokeWidth={2} /> Back to Estimate
          </button>

          <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '14px' }}>
            <div style={{ fontSize: '13px', color: STONE }}>{estimate?.customer?.name}</div>
            {generating && (
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px', color: STONE, fontSize: '12px' }}>
                <Loader2 size={12} strokeWidth={2} style={{ animation: 'spin 0.8s linear infinite' }} /> Updating…
              </div>
            )}
          </div>

          {/* ── Two-column body ── */}
          <div style={{ display: 'flex', gap: '16px', height: 'calc(100vh - 260px)', minHeight: '520px' }}>

            {/* LEFT: scrollable form */}
            <div style={{ width: '380px', flexShrink: 0, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: '10px', paddingRight: '4px' }}>

              <Card title="Contract Number">
                <input value={contractNumber} onChange={e => setContractNumber(e.target.value)}
                  name="twp-contract-number" autoComplete="off"
                  placeholder="e.g. 2026-001"
                  style={{ ...inp, fontSize: '18px', fontWeight: '800' }} />
              </Card>

              <Card title="Customer Info">
                <form onSubmit={e => e.preventDefault()} autoComplete="off" {...noAutofillContainer}
                  style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  <input type="text" name="twp-contract-antifill" tabIndex={-1} aria-hidden="true"
                    autoComplete="off" style={{ display: 'none' }} />
                  <F label="Purchaser Name">
                    <input value={customer.name} onChange={e => setCustomer(p=>({...p,name:e.target.value}))} name="twp-contract-cust-name" {...noAutofill} style={inp} />
                  </F>
                  <F label="Address">
                    <input value={customer.address} onChange={e => setCustomer(p=>({...p,address:e.target.value}))} name="twp-contract-cust-address" {...noAutofill} style={inp} />
                  </F>
                  <F label="City / State / ZIP">
                    <input value={customer.cityStateZip} onChange={e => setCustomer(p=>({...p,cityStateZip:e.target.value}))} name="twp-contract-cust-citystatezip" {...noAutofill} style={inp} />
                  </F>
                  <F label="Email">
                    <input value={customer.email} onChange={e => setCustomer(p=>({...p,email:e.target.value}))} name="twp-contract-cust-email" {...noAutofill} style={inp} />
                  </F>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' }}>
                    <F label="Home Phone">
                      <input value={customer.homePhone} onChange={e => setCustomer(p=>({...p,homePhone:fmtPhone(e.target.value)}))} name="twp-contract-cust-homephone" {...noAutofill} placeholder="(605) 000-0000" style={inp} />
                    </F>
                    <F label="Cell Phone">
                      <input value={customer.cellPhone} onChange={e => setCustomer(p=>({...p,cellPhone:fmtPhone(e.target.value)}))} name="twp-contract-cust-cellphone" {...noAutofill} placeholder="(605) 000-0000" style={inp} />
                    </F>
                    <F label="Work Phone">
                      <input value={customer.workPhone} onChange={e => setCustomer(p=>({...p,workPhone:fmtPhone(e.target.value)}))} name="twp-contract-cust-workphone" {...noAutofill} placeholder="(605) 000-0000" style={inp} />
                    </F>
                    <F label="Fax Number">
                      <input value={customer.faxNumber} onChange={e => setCustomer(p=>({...p,faxNumber:fmtPhone(e.target.value)}))} name="twp-contract-cust-fax" {...noAutofill} placeholder="(605) 000-0000" style={inp} />
                    </F>
                  </div>
                </form>
              </Card>

              {/* Project Description — grows with content, proper line breaks */}
              <Card title="Project Description">
                <div style={{ fontSize: '11px', color: STONE, marginBottom: '6px' }}>Press Enter for new lines. Each line will appear on the contract.</div>
                <textarea
                  value={projectDescription}
                  onChange={e => setProjectDescription(e.target.value)}
                  placeholder={"Line 1: Window type and location\nLine 2: Door details\nLine 3: Additional notes…"}
                  style={{
                    ...inp,
                    resize: 'vertical',
                    minHeight: '100px',
                    fontSize: '13px',
                    lineHeight: '1.7',
                    padding: '10px 12px',
                    whiteSpace: 'pre-wrap',
                    fontFamily: FONT_SANS,
                  }}
                />
              </Card>

              <Card title="Includes">
                <CB label="Low-E Argon Gas" checked={checkboxes.lowE}       onChange={v => setCheckboxes(p=>({...p,lowE:v}))} />
                <CB label="Screen"          checked={checkboxes.screen}      onChange={v => setCheckboxes(p=>({...p,screen:v}))} />
                <CB label="Outside Trim"    checked={checkboxes.outsideTrim} onChange={v => setCheckboxes(p=>({...p,outsideTrim:v}))} />
                <CB label="Brickmold"       checked={checkboxes.brickmold}   onChange={v => setCheckboxes(p=>({...p,brickmold:v}))} />
                <CB label="1x4"             checked={checkboxes.oneByFour}   onChange={v => setCheckboxes(p=>({...p,oneByFour:v}))} />
              </Card>

              <Card title="Installation & Details">
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  <F label="Approx. Install Month">
                    <select value={installMonth} onChange={e => setInstallMonth(e.target.value)} style={sel}>
                      <option value="">Select month</option>
                      {MONTHS.map(m => <option key={m} value={m}>{m}</option>)}
                    </select>
                  </F>
                  <F label="Aluminum Wrap Color">
                    <input value={wrapColor} onChange={e => setWrapColor(e.target.value)} name="twp-contract-wrapcolor" autoComplete="off" style={inp} />
                  </F>
                  <F label="Trash Removal">
                    <select value={trash} onChange={e => setTrash(e.target.value)} style={sel}>
                      <option value="Yes">Yes</option>
                      <option value="No">No</option>
                    </select>
                  </F>
                </div>
              </Card>

              <Card title="Terms of Payment">
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  <F label="(1) Cash Price (incl. tax)">
                    <div style={{ position: 'relative' }}>
                      <span style={{ position: 'absolute', left: '11px', top: '50%', transform: 'translateY(-50%)', color: '#888' }}>$</span>
                      <input value={cashPrice} onChange={e => setCashPrice(e.target.value)} name="twp-contract-cashprice" autoComplete="off" style={{ ...inp, paddingLeft: '22px' }} />
                    </div>
                    {fmtMoney(cashPrice) !== cashPrice && <div style={{ fontSize: '11px', color: MOSS, marginTop: '2px' }}>→ ${fmtMoney(cashPrice)}</div>}
                  </F>
                  <F label="(2) Down Payment (1/3)">
                    <div style={{ position: 'relative' }}>
                      <span style={{ position: 'absolute', left: '11px', top: '50%', transform: 'translateY(-50%)', color: '#888' }}>$</span>
                      <input value={downPayment} onChange={e => setDownPayment(e.target.value)} name="twp-contract-downpayment" autoComplete="off" style={{ ...inp, paddingLeft: '22px' }} />
                    </div>
                    {fmtMoney(downPayment) !== downPayment && <div style={{ fontSize: '11px', color: MOSS, marginTop: '2px' }}>→ ${fmtMoney(downPayment)}</div>}
                  </F>
                  <F label="(3) Unpaid Balance (auto)">
                    <div style={{ position: 'relative' }}>
                      <span style={{ position: 'absolute', left: '11px', top: '50%', transform: 'translateY(-50%)', color: '#888' }}>$</span>
                      <input value={fmtMoney(unpaidBalance)} readOnly style={{ ...inp, paddingLeft: '22px', background: '#f5f5f5', color: '#555' }} />
                    </div>
                  </F>
                </div>
              </Card>

              <Card title="Signature Date">
                <div style={{ display: 'flex', gap: '8px' }}>
                  <F label="Day">
                    <input value={sigDay} onChange={e => setSigDay(e.target.value)} name="twp-contract-sigday" autoComplete="off" placeholder="26" style={{ ...inp, width: '56px', textAlign: 'center' }} />
                  </F>
                  <div style={{ flex: 1 }}>
                    <F label="Month">
                      <select value={sigMonth} onChange={e => setSigMonth(e.target.value)} style={sel}>
                        <option value="">Month</option>
                        {MONTHS.map(m => <option key={m} value={m}>{m}</option>)}
                      </select>
                    </F>
                  </div>
                  <F label="Year (20__)">
                    <input value={sigYear} onChange={e => setSigYear(e.target.value)} name="twp-contract-sigyear" autoComplete="off" placeholder="26" style={{ ...inp, width: '62px', textAlign: 'center' }} />
                  </F>
                </div>
              </Card>

              <Card title="Your Signature (Representative)">
                <div style={{ fontSize: '11px', color: STONE, marginBottom: '8px' }}>Draw your signature — it will be stamped on the contract.</div>
                <SignaturePad value={repSignature} onChange={setRepSignature} width={336} height={90} />
              </Card>

              <button onClick={handleSend} disabled={sending || !previewBlob}
                style={{
                  display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px',
                  padding: '14px', borderRadius: '10px', border: 'none',
                  background: (!previewBlob || sending) ? '#ccc' : WALNUT,
                  color: 'white', fontSize: '14px', fontWeight: '700',
                  cursor: (!previewBlob || sending) ? 'not-allowed' : 'pointer',
                  fontFamily: FONT_SANS, marginBottom: '32px', flexShrink: 0,
                }}>
                {sending ? <><Loader2 size={15} strokeWidth={2} style={{ animation: 'spin 0.8s linear infinite' }} /> Sending…</> : <><Send size={15} strokeWidth={2} /> Send Contract to Customer</>}
              </button>

            </div>

            {/* RIGHT: fixed PDF preview */}
            <div style={{ flex: 1, overflow: 'hidden', background: '#525659', position: 'relative', borderRadius: '12px' }}>
              {generating && (
                <div style={{ position: 'absolute', top: '12px', left: '50%', transform: 'translateX(-50%)', background: 'rgba(0,0,0,0.65)', color: 'white', fontSize: '12px', padding: '6px 16px', borderRadius: '20px', zIndex: 10, display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <Loader2 size={12} strokeWidth={2} style={{ animation: 'spin 0.8s linear infinite' }} />
                  Updating preview…
                </div>
              )}
              {previewUrl ? (
                <iframe
                  key={previewUrl}
                  src={`${previewUrl}#toolbar=0&navpanes=0`}
                  style={{ width: '100%', height: '100%', border: 'none', display: 'block' }}
                  title="Contract Preview"
                />
              ) : (
                <div style={{ height: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: '12px', color: 'rgba(255,255,255,0.45)' }}>
                  <FileText size={36} strokeWidth={1.5} />
                  <div style={{ fontSize: '14px' }}>Preview loads automatically as you fill in the fields</div>
                </div>
              )}
            </div>

          </div>
        </div>
      )}

      {/* Sending overlay */}
      {sending && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)', zIndex: 9999, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: '16px' }}>
          <Loader2 size={40} strokeWidth={2} color="white" style={{ animation: 'spin 0.8s linear infinite' }} />
          <div style={{ color: 'white', fontSize: '15px', fontWeight: '600', fontFamily: FONT_SANS }}>Sending contract…</div>
        </div>
      )}
    </AppLayout>
  );
}
